/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: ImportFeature.tsx
 * Project: mle-client-n
 * Committed last: 2022/11/24 @ 1810
 * ---
 * Description:
 */

import {
    AdvancedFieldIterator,
    DataRefType,
    IDynamicDataRef,
    IRawData,
    MetaGroup,
    MetaKey,
    MpesInstanceManager
} from '@revodigital/mpes';
import {ActiveConfig} from '../../agents/ProjectCreationAgent';
import {Label} from '../../../entities';
import {mleDomainGqlClient} from '../../../api/domain';
import {CREATE_LABEL} from '../../../api/domain/mutations.gql';
import {Metadata} from '@revodigital/mpes/lib';
import {ACTIVE_COMPANY, ACTIVE_RECIPE, ACTIVE_STATE_GROUP} from '../mylabel/RevisionSwitcherFeature';
import {StandaloneFeature} from '../common/StandaloneFeature';

export const IMPORT_FNAME = 'ImportFeature';

export interface ImportParams {
    /**
     * Configurazione relativa alla revisione attiva
     */
    activeConfig?: ActiveConfig;
    dataOriginId?: string;
    /**
     * Placeholder testuale da impostare alle dataorigin quando non le si vuole importare
     */
    dataOriginPlaceHolder?: string;
}

/**
 * Funzionalità per gestire l'importazione delle etichette
 */
export class ImportFeature extends StandaloneFeature<void> {
    constructor() {
        super(IMPORT_FNAME);
    }

    /**
     * Importa un'etichetta a partire da un documento Mpes codificato
     * @param document
     * @param importParams parametri per l'import, come ad esempio la revisione attiva.
     */
    public async importFromMpesDocument(documentBuffer: string, importParams: ImportParams): Promise<Label | undefined> {
        // Decode document
        const document = await MpesInstanceManager.getDecoderInstance().decodeBuffer(
            documentBuffer);

        // Configuro il necessario per le data origin
        if (importParams.dataOriginId) this.configDataOrigins(document, importParams.activeConfig!!, importParams.dataOriginId!!);

        document.header.title = `[imp] ${document.header.title}`;

        // Creazione etichetta e salvataggio della configurazione attiva
        try {
            return await this.createLabel(document);
        } catch (e) {
            console.error(`Impossibile importare etichetta ${document.header.title}. Dati decodificati: ${JSON.stringify(
                document)}`);
            console.error(e);

            return undefined;
        }
    }

    /**
     * Formatta i metadati per il documento
     * @param activeConfig
     * @private
     */
    private formatMetadata(activeConfig: ActiveConfig): Metadata {
        const meta = new Metadata();
        const g = new MetaGroup({
            title: ACTIVE_STATE_GROUP,
            keys: [MetaKey.init(ACTIVE_RECIPE,
                activeConfig.recipeId), MetaKey.init(
                ACTIVE_COMPANY,
                activeConfig.companyId)],
            payload: {}
        });
        meta.addGroup(g);

        return meta;
    }

    /**
     * Rimuove le origini dati dal documento
     * @param document
     * @param placeholder
     * @private
     */
    private removeDataOrigins(document: IRawData, placeholder?: string) {
        // Rimuovo tutti i dataref
        document.dataRefs = [];

        AdvancedFieldIterator.forEachRawShapeField(document.content.data, field => {
            if (field.value) {
                const val = field.value as any;
                if (val.startsWith("{{") && val.endsWith("}}")) field.setValue(placeholder || "-");
            }
        });
    }

    private configDataOrigins(document: IRawData, activeConfig: ActiveConfig, dataOriginId: string) {
        if (!activeConfig) throw new Error("Error importing label. Active config not specified.");

        // Collego tutti i dataref all'origine dati specificata
        document.dataRefs?.forEach(ref => {
            if (ref.type === DataRefType.DYNAMIC)
                (ref as IDynamicDataRef).dataOriginId = dataOriginId;
        });

        // Aggiungo i metadati
        document.metadata = this.formatMetadata(activeConfig);
    }

    /**
     * Crea effettivamente l'etichetta
     * @param document
     * @private
     */
    private async createLabel(document: IRawData): Promise<Label> {
        const label = (await mleDomainGqlClient.mutate({
            mutation: CREATE_LABEL,
            variables: {
                bodyContent: JSON.stringify(await MpesInstanceManager.getEncoderInstance().encodeDocument(
                    document)),
                title: document.header.title
            }
        })).data.addLabel as Label;

        return label;
    }
}
